import React, { useContext, useMemo } from "react";
import ReactECharts from "echarts-for-react";
import { format } from "date-fns";
import { ColorModeContext } from "../../theme/ColorModeContext";
import { chartAxisStyle, getChartTheme } from "./chartTheme";

const WeightChart = (Props: any) => {
  // PROPS
  const {
    weightList,
    weightChartZoneData,
    isMobileScreen,
    isDownload,
  } = Props;
  const { mode } = useContext(ColorModeContext);
  const chartTheme = useMemo(
    () => getChartTheme(mode, Boolean(isDownload)),
    [mode, isDownload],
  );

  // ✅ Use memoization for derived data to prevent recalculation on each render
  const {
    currentWeight,
    zoneCenterLine,
    yAxisMaxPoint,
    maxDate,
    lastZone,
    lastZoneTime,
  } = useMemo(() => {
    const filteredWeights = weightList
      ?.filter((item: any) => item?.weight && item?.weight > 0)
      ?.map((item: any) => [new Date(item?.chartLabel)?.getTime(), item?.weight]);

    const maxDate = Math.max(...filteredWeights.map((d: any) => d[0]));
    const lastZone = weightChartZoneData[weightChartZoneData.length - 1];
    const lastZoneTime = new Date(lastZone?.ageDate)?.getTime();

    const zoneCenterLine = weightChartZoneData
      .filter((h: any) => new Date(h?.ageDate)?.getTime() <= maxDate)
      .map((h: any) => [new Date(h?.ageDate)?.getTime(), h.zoneCenterGreenValue]);

    if (lastZoneTime < maxDate) {
      zoneCenterLine.push([maxDate, lastZone.zoneCenterGreenValue]);
    }

    const maxWeightZoneValue =
      weightChartZoneData?.reduce(
        (curr: number, item: any) => Math.max(curr, item.zoneUpperRedMaxValue ?? 0),
        0
      ) || 0;

    const maxWeight = filteredWeights?.reduce(
      (curr: number, item: any) => Math.max(curr, item[1] ?? 0),
      0
    ) || 0;

    const yAxisMaxPoint = Math.ceil(Math.max(maxWeightZoneValue, maxWeight) / 10) * 10;

    return { currentWeight: filteredWeights, zoneCenterLine, yAxisMaxPoint, maxDate, lastZone, lastZoneTime };
  }, [weightList, weightChartZoneData]);

  const hasData = currentWeight?.length > 0;

  if (!hasData) {
    return null;   // hide chart if no data
  }

  // Convert zone data to polygon format
  const createZonePolygon = (
    name: string,
    minKey: string,
    maxKey: string,
    color: string
  ) => {
    const maxPoints = weightChartZoneData.map((p: any) => [new Date(p?.ageDate).getTime(), p[maxKey]]);
    const minPoints = weightChartZoneData.map((p: any) => [new Date(p?.ageDate).getTime(), p[minKey]]).reverse();

    if (lastZoneTime < maxDate) {
      maxPoints.push([maxDate, lastZone[maxKey]]);
      minPoints.unshift([maxDate, lastZone[minKey]]);
    }

    const polygonPoints = maxPoints.concat(minPoints);

    return {
      name,
      type: "line",
      data: polygonPoints,
      lineStyle: { width: 0 },
      symbol: "none",
      areaStyle: { color, opacity: 0.3 },
      smooth: true,
      tooltip: { show: false },
      z: 1,
      animation: false, // Disable animation
    };
  };

  const color = {
    red: "#FF0000",
    orange: "#FFA500",
    yellow: "#FFFF00",
    green: "#008000",
  };

  const WeightOption = {
    animation: false, //  Disable global animation
    animationDuration: 0,
    animationEasing: "linear",
    animationThreshold: 0,
    progressive: 0,
    hoverLayerThreshold: 0,
    backgroundColor: "transparent",
    textStyle: { color: chartTheme.text },
    tooltip: {
      trigger: "axis",
      backgroundColor: chartTheme.tooltipBg,
      borderColor: chartTheme.tooltipBorder,
      borderWidth: 1,
      textStyle: {
        color: chartTheme.tooltipText,
        fontSize: 12,
      },
      formatter: function (params: any) {
        const dotColors = {
          "Current Weight": chartTheme.currentLine,
        };

        const firstPoint = params[0].value[0];
        const seriesData = weightList.find(
          (x: any) => new Date(x.chartLabel).getTime() === firstPoint,
        );

        const age = seriesData ? seriesData.age : null;

        let html = `<div style="text-align:left;color:${chartTheme.tooltipText};">`;
        html += `<div style="font-weight:bold; margin-bottom:4px;">
          ${format(new Date(params[0].value[0]), "dd-MM-yyyy")}
          ${age ? `(${age})` : ""}
        </div>`;

        params.forEach((item: any) => {
          const color = dotColors[item.seriesName] || item.color;
          html += `
            <div style="margin-bottom:2px;">
              <span style="width:8px; height:8px; border-radius:50%; background-color:${color}; display:inline-block; margin-right:6px;"></span>
              <span>${item.seriesName}: <strong>${item.value[1]}</strong> kg</span>
            </div>`;
        });

        html += `</div>`;
        return html;
      },
    },
    xAxis: {
      type: "time",
      name: "Age",
      nameLocation: "middle",
      nameGap: 30,
      ...chartAxisStyle(chartTheme),
    },
    yAxis: {
      type: "value",
      min: 0,
      max: yAxisMaxPoint,
      name: "Weight (kg)",
      nameLocation: "middle",
      nameRotate: 90,
      interval: Math.ceil(yAxisMaxPoint / 20),
      nameGap: 40,
      ...chartAxisStyle(chartTheme),
    },
    title: {
      text: "Weight Chart",
      left: "center",
      top: isMobileScreen ? 5 : 10, // small offset
      textStyle: {
        fontSize: isMobileScreen ? 13 : 18,
        fontWeight: 600,
        color: chartTheme.text,
      },
    },
    legend: {
      data: [
        { name: "97-99 Percentile", itemStyle: { color: "#FF0000" } },
        { name: "91-96 Percentile", itemStyle: { color: "#FFA500" } },
        { name: "76-90 Percentile", itemStyle: { color: "#FFFF00" } },
        { name: "26-75 Percentile", itemStyle: { color: "#008000" } },
        { name: "50 Percentile", itemStyle: { color: "#008000" } },
        {
          name: "Current Weight",
          itemStyle: { color: chartTheme.currentLine },
        },
        { name: "11-25 Percentile", itemStyle: { color: "#FFFF00" } },
        { name: "4-10 Percentile", itemStyle: { color: "#FFA500" } },
        { name: "1-3 Percentile", itemStyle: { color: "#FF0000" } },
      ],
      type: "plain",
      orient: isMobileScreen ? "horizontal" : "vertical",
      top: isMobileScreen ? 25 : "middle",
      left: isMobileScreen ? "center" : "auto",
      right: isMobileScreen ? "auto" : 10,
      bottom: isMobileScreen ? "auto" : "auto",
      align: isMobileScreen ? "auto" : "left",
      itemWidth: 14,
      itemHeight: 10,
      textStyle: {
        fontSize: isMobileScreen ? 10 : 11,
        color: chartTheme.text,
      },
      pageTextStyle: { color: chartTheme.text },
      padding: isMobileScreen ? [5, 5, 5, 5] : 0,
    },
    grid: {
      top: isMobileScreen ? 130 : 50,
      right: isMobileScreen ? 0 : 130,
      left: isMobileScreen ? 60 : 100,
    },
    dataZoom: isDownload
      ? [] // hide sliders when downloading
      : [
        // Horizontal zoom (x-axis)
        {
          type: 'inside',
          xAxisIndex: 0,
          zoomOnMouseWheel: true,
          moveOnMouseMove: true,
        },
        {
          type: 'slider',
          xAxisIndex: 0,
          start: 0,
          end: 100,
          bottom: isMobileScreen ? 10 : 0, // ✅ slightly above bottom for mobile
          handleSize: 8,
          handleStyle: { borderWidth: 1 },
          borderWidth: 1,
          height: 12, // thinner horizontal slider
        },
        // Vertical zoom (y-axis)
        {
          type: 'inside',
          yAxisIndex: 0,
          zoomOnMouseWheel: true,
          moveOnMouseMove: true,
        },
        // {
        //   type: 'slider',
        //   yAxisIndex: 0,
        //   start: 0,
        //   end: 100,
        //   left: isMobileScreen ? 40 : 80, // ✅ push closer on mobile
        //   orient: 'vertical',
        //   width: 8, // ✅ thinner vertical line
        //   handleSize: 8,
        //   handleStyle: { borderWidth: 1 },
        //   borderWidth: 1,
        // },
      ],
    series: [
      // Zones as polygons
      createZonePolygon(
        '1-3 Percentile',
        'zoneLowerRedMinValue',
        'zoneLowerRedMaxValue',
        color.red
      ),
      createZonePolygon(
        '4-10 Percentile',
        'zoneLowerOrangeMinValue',
        'zoneLowerOrangeMaxValue',
        color.orange
      ),
      createZonePolygon(
        '11-25 Percentile',
        'zoneLowerYellowMinValue',
        'zoneLowerYellowMaxValue',
        color.yellow
      ),
      createZonePolygon(
        '26-75 Percentile',
        'zoneLowerGreenMinValue',
        'zoneLowerGreenMaxValue',
        color.green
      ),
      createZonePolygon(
        '26-75 Percentile',
        'zoneUpperGreenMinValue',
        'zoneUpperGreenMaxValue',
        color.green
      ),
      createZonePolygon(
        '76-90 Percentile',
        'zoneUpperYellowMinValue',
        'zoneUpperYellowMaxValue',
        color.yellow
      ),
      createZonePolygon(
        '91-96 Percentile',
        'zoneUpperOrangeMinValue',
        'zoneUpperOrangeMaxValue',
        color.orange
      ),
      createZonePolygon(
        '97-99 Percentile',
        'zoneUpperRedMinValue',
        'zoneUpperRedMaxValue',
        color.red
      ),

      // Center green dashed line
      {
        name: '50 Percentile',
        type: 'line',
        // data: weightChartZoneData.map(h => [
        //   new Date(h.ageDate).getTime(),
        //   h.zoneCenterGreenValue
        // ]),
        data: zoneCenterLine,
        lineStyle: {
          color: color.green,
          width: 1,
          type: 'solid'
        },
        showSymbol: false,
        tooltip: { show: false },
        z: 3,
        animation: false,//  Disable line animation
      },
      {
        name: "Current Weight",
        type: "line",
        data: currentWeight,
        lineStyle: {
          type: "solid",
          width: 2,
          color: chartTheme.currentLine,
        },
        symbol: "circle",
        itemStyle: {
          color: chartTheme.currentLine,
        },
        symbolSize: 6,
        z: 4,
        smooth: true,
        animation: false, //  Disable line animation
      },
    ],
  };

  return (
    <div
      style={{
        width: "100%",
        height: "100%",
      }}
    >
      <ReactECharts
        option={WeightOption}
        style={{
          height: isDownload ? "750px" : isMobileScreen ? "70vh" : "85vh",
        }}
        notMerge={true}
        lazyUpdate={true}
        opts={{ renderer: "canvas" }}
      />
    </div>
  );
};


export default React.memo(WeightChart);
